/**
 * Keeps the "Free gift with your order" tick in step with the purchase type when the
 * product section is used on a landing page. Mirrors the behaviour that
 * sections/bundle-sticky-atc.liquid provides on the product templates:
 * - Subscribe selected: gift ticked. One-time selected: gift unticked.
 * - Clicking an unticked gift switches back to Subscribe and scrolls to that card.
 * The tick is a button in landing mode, enabled only while unticked, so keyboard users
 * have the same route back to Subscribe.
 */
if (!customElements.get('bundle-free-gift-sync')) {
  customElements.define(
    'bundle-free-gift-sync',
    class BundleFreeGiftSync extends HTMLElement {
      constructor() {
        super();
        this.onPurchaseChange = this.onPurchaseChange.bind(this);
        this.onGiftClick = this.onGiftClick.bind(this);
      }

      connectedCallback() {
        this.root = this.closest('product-info') || document;
        this.root.addEventListener('change', this.onPurchaseChange);
        this.addEventListener('click', this.onGiftClick);

        const selected = this.root.querySelector('.bundle-purchase-card__radio-input:checked');
        if (selected) this.setTicked(selected.value !== 'one-time');
      }

      disconnectedCallback() {
        this.root?.removeEventListener('change', this.onPurchaseChange);
        this.removeEventListener('click', this.onGiftClick);
      }

      /** @returns {HTMLButtonElement | null} */
      get check() {
        return this.querySelector('.bundle-free-gift__check');
      }

      /** @param {boolean} isTicked */
      setTicked(isTicked) {
        const check = this.check;
        if (!check) return;

        check.classList.toggle('unchecked', !isTicked);
        check.disabled = isTicked;
      }

      /** @param {Event} event */
      onPurchaseChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.classList.contains('bundle-purchase-card__radio-input')) return;
        if (!input.checked) return;

        if (input.value === 'subscribe') this.setTicked(true);
        if (input.value === 'one-time') this.setTicked(false);
      }

      /** @param {MouseEvent} event */
      onGiftClick(event) {
        event.preventDefault();
        if (!this.check?.classList.contains('unchecked')) return;

        const subscribeCard = this.root.querySelector('.bundle-purchase-card[data-bundle-purchase-card="subscribe"]');
        const subscribeRadio = subscribeCard?.querySelector('.bundle-purchase-card__radio-input');
        if (!subscribeRadio || subscribeRadio.checked) return;

        // Let the real radio's change event drive the state, so <bundle-purchase-options>
        // updates the active card, the hidden selling_plan input and the header price together.
        subscribeRadio.checked = true;
        subscribeRadio.dispatchEvent(new Event('change', { bubbles: true }));

        // The tick button is disabled again now, so hand keyboard focus to the option it chose.
        subscribeRadio.focus({ preventScroll: true });

        // Measure after the subscribe card has expanded, or the scroll target is too short.
        requestAnimationFrame(() => {
          const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
          const breathingRoom = 24;
          const top = subscribeCard.getBoundingClientRect().top + window.scrollY - headerHeight - breathingRoom;

          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

          window.scrollTo({ top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
        });
      }
    },
  );
}
